import { ProColumns } from '@ant-design/pro-components';
import { Link, useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import * as Icon from 'base/ui/components/Icons';
import CustomButton from 'base/ui/components/Button';
import CustomButtonIcon from 'base/ui/components/Button/buttonIcon';
import { WasteHandoverPlanModel } from 'services/Main/wasteHandoverPlan_services';
import { formatDeliveryDateDisplay } from '../utils/dateHelpers';
import { DATE_HOUR_MINUTE_FORMAT_DEFAULT } from 'utils/constants';
import dayjs from 'dayjs';
import { ROUTES } from 'base/routes/routes';
import { CopyOutlined } from '@ant-design/icons';

/**
 * Props cho component định nghĩa các cột của table
 */
interface TableColumnsProps {
  getColumnSearchProps: (
    dataIndex: keyof WasteHandoverPlanModel,
    type?: 'date',
    filterCondition?: (record: WasteHandoverPlanModel) => boolean
  ) => ProColumns<WasteHandoverPlanModel>;
  isEdit: boolean;
  isCreate: boolean;
  isDelete: boolean;
  onDelete: (record: WasteHandoverPlanModel) => void;
  selectionConstraint?: {
    date: string;
    vendorNumber: string;
    wasteGroup: string;
  } | null;
}

/**
 * Hook để định nghĩa các cột cho table Waste Handover Plan
 */
export const useTableColumns = ({
  getColumnSearchProps,
  isEdit,
  isCreate,
  isDelete,
  onDelete,
  selectionConstraint,
}: TableColumnsProps): ProColumns<WasteHandoverPlanModel>[] => {
  const { t } = useTranslation();

  const navigate = useNavigate()

  return [
    {
      title: t('waste-handover-plan.stt'),
      dataIndex: 'stt',
      key: 'stt',
      width: 60,
      align: 'center',
      fixed: 'left',
    },
    {
      title: t('waste-handover-plan.delivery-schedule-time'),
      dataIndex: 'deliveryDate',
      key: 'deliveryDate',
      align: 'center',
      width: 200,
      className: 'min-w-[200px]',
      valueType: 'dateTime',
      fieldProps: {
        format: DATE_HOUR_MINUTE_FORMAT_DEFAULT,
      },
      render: (_, record) => {
        return formatDeliveryDateDisplay(record.deliveryDate) || '-';
      },
      ...getColumnSearchProps('deliveryDate', 'date'),
    },
    {
      title: t('masterdata.waste-grouping-contract.title'),
      dataIndex: 'wasteGroupingContractName',
      key: 'wasteGroupingContractName',
      width: 260,
      className: 'min-w-[240px]',
      render: (_, record) => record.wasteGroupingContractName || '-',
      ...getColumnSearchProps('wasteGroupingContractName'),
    },
    {
      title: t('waste-handover-plan.waste-code'),
      dataIndex: 'productCode',
      key: 'productCode',
      width: 170,
      className: 'min-w-[170px]',
      ...getColumnSearchProps('productCode'),
    },
    {
      title: t('waste-handover-plan.waste-name'),
      dataIndex: 'materialName',
      key: 'materialName',
      width: 240,
      className: 'min-w-[220px]',
      ...getColumnSearchProps('materialName'),
    },
    {
      title: t('waste-handover-plan.vendor'),
      dataIndex: 'vendorName',
      key: 'vendorName',
      width: 260,
      className: 'min-w-[220px]',
      render: (_, record) => {
        if (record.vendorNumber && record.vendorName) {
          return `${record.vendorNumber} | ${record.vendorName}`;
        }
        return record.vendorName || '-';
      },
      ...getColumnSearchProps('vendorName'),
    },
    {
      title: t('waste-handover-plan.storage-location'),
      dataIndex: 'storageLocation',
      key: 'storageLocation',
      width: 200,
      className: 'min-w-[180px]',
      ...getColumnSearchProps('storageLocation'),
      render: (_, record) => record.storageLocationName || record.storageLocation || '-',
    },
    {
      title: t('waste-handover-plan.delivery-number'),
      dataIndex: 'handoverDocumentNumber',
      key: 'handoverDocumentNumber',
      width: 160,
      className: 'min-w-[160px]',
      ...getColumnSearchProps('handoverDocumentNumber'),
    },
    {
      title: t('waste-handover-plan.note'),
      dataIndex: 'note',
      key: 'note',
      width: 180,
      className: 'min-w-[160px]',
      render: (_, record) => record.note || '-',
    },
    {
      title: t('waste-handover-plan.created-time'),
      dataIndex: 'createTime',
      key: 'createTime',
      width: 180,
      className: 'min-w-[180px]',
      align: 'center',
      valueType: 'dateTime',
      fieldProps: {
        format: 'DD/MM/YYYY HH:mm:ss',
      },
      render: (_, record) => {
        if (!record.createTime) return '-';
        // Parse date trực tiếp, không convert timezone để hiển thị đúng như API
        const date = dayjs(record.createTime);
        return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : record.createTime;
      },
    },
    {
      title: t('waste-handover-plan.creator'),
      dataIndex: 'createByName',
      key: 'createByName',
      width: 150,
      className: 'min-w-[140px]',
      ...getColumnSearchProps('createByName'),
      render: (_, record) => record.createByName || '-',
    },
    {
      title: t('waste-handover-plan.cancel-time'),
      dataIndex: 'lastEditTime',
      key: 'lastEditTime',
      width: 180,
      className: 'min-w-[180px]',
      align: 'center',
      render: (_, record) => {
        if (record.actived === false && record.lastEditTime) {
          const date = dayjs(record.lastEditTime);
          return date.isValid() ? date.format('DD/MM/YYYY HH:mm:ss') : record.lastEditTime;
        }
        return '-';
      },
    },
    // người hủy
    {
      title: t('waste-handover-plan.cancel-by'),
      dataIndex: 'lastEditByName',
      key: 'lastEditByName',
      width: 160,
      className: 'min-w-[150px]',
      ...getColumnSearchProps('lastEditByName', undefined, (record) => record.actived === false),
      render: (_, record) => {
        if (record.actived === false && record.lastEditByName) {
          return record.lastEditByName;
        }
        return '-';
      },
    },
    // trạng thái
    {
      title: t('waste-handover-plan.status'),
      dataIndex: 'actived',
      key: 'actived',
      width: 100,
      className: 'min-w-[100px]',
      align: 'center',
      render: (_, record) => (
        <div className="flex justify-center">
          {record.actived ? (
            <Icon.CheckStatus className="!fill-[#389e0d]" width="15" />
          ) : (
            <Icon.CloseStatus className="!fill-[#D93133E5]" width="15" />
          )}
        </div>
      ),
    },
    {
      title: t('waste-handover-plan.actions'),
      dataIndex: 'functions',
      key: 'functions',
      width: 100,
      className: 'min-w-[100px]',
      align: 'center',
      fixed: 'right',
      render: (_, record) => {
        // Điều kiện hiển thị nút sửa/xóa giống checkbox
        const shouldShowButtons =
          record.actived === true &&
          record.isRegistered !== true &&
          (selectionConstraint
            ? (
              dayjs(record.deliveryDate).format('YYYY-MM-DD') === selectionConstraint.date &&
              record.vendorNumber === selectionConstraint.vendorNumber &&
              (record.wasteGroup || record.wasteGroupingContractName || '') === selectionConstraint.wasteGroup
            )
            : true);

        return (
          <div className="w-full flex items-center justify-center gap-x-1">
            <Tooltip title={'Sao chép'}>
              {isCreate && (
                <CustomButtonIcon
                  color="#FF69B4"
                  onClick={() => navigate(`${ROUTES.WASTE_HANDOVER_PLAN.COPY.LINK}/${record.wasteHandoverPlanId}`)}
                // className={`${record.actived ? '!block' : '!invisible'} !leading-[25px] !h-[25px]`}
                >
                  <CopyOutlined className="!text-[#FF69B4] flex" />
                </CustomButtonIcon>
              )}
            </Tooltip>
            {isEdit && shouldShowButtons && (
              <Tooltip title={t('btn.edit')}>
                <Link to={`/WasteHandoverPlan/${record.wasteHandoverPlanId}`}>
                  <CustomButtonIcon color="#007BF1">
                    <Icon.PencilEdit className="!fill-[#007BF1]" width="14" height="14" />
                  </CustomButtonIcon>
                </Link>
              </Tooltip>
            )}
            {isDelete && shouldShowButtons && (
              <Tooltip title={t('btn.delete')}>
                <CustomButton
                  className="!w-6 !h-6 rounded-sm !border-[#FF4F51] flex gap-1 items-center justify-center !bg-[#FFE5E6] hover:bg-[#FF8081]"
                  icon={<Icon.Trash className="!fill-[#FC5C5E]" width="16" height="16" />}
                  onClick={() => onDelete(record)}
                />
              </Tooltip>
            )}
          </div>
        );
      },
    },
  ];
};

